import React, { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Grid,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { CONTENT_ALIAS, QUESTION_TYPES } from '../../config/const.config';
import QuestionTypes from '../form-elements/question-types.component';
import OptionsList from './options-list.component';
import {
  getQuizQuestionById,
  saveQuizQuestionDetail,
} from '../../services/quiz.service';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddEditQuizQuestion = ({
  title,
  dataId,
  quizId,
  onCancel,
  onSuccess,
  showDeletePrompt,
}) => {
  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      questionText: '',
      type: QUESTION_TYPES.SINGLE_RADIO.value,
      explanation: '',
      placeholder: null,
      options: [],
    },
  });

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const watchType = watch('type');

  const saveNewData = (payload) => {
    saveQuizQuestionDetail(payload)
      .then((res) => {
        onSuccess(`New ${CONTENT_ALIAS.QUIZ_QUESTION} added successfully.`, {
          ...payload,
          ...res.data,
        });
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    saveQuizQuestionDetail(payload)
      .then((res) => {
        onSuccess(`${CONTENT_ALIAS.QUIZ_QUESTION} updated successfully.`, {
          ...payload,
          ...res.data,
        });
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const handleActionSuccess = (message = '') => {
    setSnackbarInfo({ ...snackbarInfo, show: false });
    if (message) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message,
        });
      }, 10);
    }
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
      type: '',
      message: '',
    });

    if (data.options.length < 2) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'error',
        message: 'At least 2 options are required.',
      });
      setFormSubmitted(false);

      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: false,
          type: '',
          message: '',
        });
      }, 3000);
      return;
    }

    // Check if at least one option is marked as correct
    const hasCorrectOption = data.options.some((option) => option.isTrue === 1);
    if (!hasCorrectOption && watchType !== QUESTION_TYPES.MATCH.value) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'error',
        message: 'Please select at least one correct option.',
      });
      setFormSubmitted(false);

      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: false,
          type: '',
          message: '',
        });
      }, 3000);
      return;
    }

    const payload = {
      quizId,
      questionId: dataId || null,
      questionText: data.questionText,
      explanation: data.explanation || null,
      placeholder: data.placeholder || null,
      type: data.type,
      options:
        data.options.map((item, optionIndex) => ({
          ...item,
          optionId: item.optionId || null,
          questionId: dataId || null,
          sequence: optionIndex + 1,
          isTrue:
            watchType === QUESTION_TYPES.MATCH.value ? 1 : item.isTrue || 0,
        })) || [],
    };

    if (dataId) {
      updateExistingData(payload);
    } else {
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }

    getQuizQuestionById(dataId)
      .then((res) => {
        setValue('questionText', res.data?.questionText || '');
        setValue('type', res.data?.type || null);
        setValue('explanation', res.data?.explanation || '');
        setValue('placeholder', res.data?.placeholder || '');
        setValue('options', res.data?.options || []);
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="view-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        {loading && <Loading />}

        {!loading && (
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  control={control}
                  id="questionText"
                  name="questionText"
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(500),
                  }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      label="Question"
                      type="text"
                      onChange={onChange}
                      value={value}
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message || null}
                      fullWidth
                      multiline
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  control={control}
                  id="type"
                  name="type"
                  rules={validationRules.REQUIRED}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <QuestionTypes
                      id="type"
                      name="type"
                      label="Type"
                      defaultValue={value}
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      error={error?.message || ''}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  control={control}
                  id="explanation"
                  name="explanation"
                  rules={{
                    ...maxLengthValidation(1500),
                  }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      label="Explanation"
                      type="text"
                      onChange={onChange}
                      value={value}
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message || null}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  )}
                />
              </Grid>

              <OptionsList
                control={control}
                handleActionSuccess={handleActionSuccess}
                type={watchType}
              />

              {watchType === QUESTION_TYPES.SINGLE_RADIO.value && dataId ? (
                <Grid
                  item
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 2,
                  }}
                >
                  <Box sx={{ fontWeight: 'bold' }}>Note:&nbsp;</Box>
                  When selecting answers in this module, please proceed
                  cautiously. Modifying them after saving may result in data
                  errors.
                </Grid>
              ) : null}

              {watchType === QUESTION_TYPES.MATCH.value ? (
                <Grid
                  item
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 2,
                  }}
                >
                  <Box sx={{ fontWeight: 'bold' }}>Note:&nbsp;</Box>
                  Please assign the correct answers directly next to the options
                  for better clarity and ease of use.
                </Grid>
              ) : null}
            </Grid>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'left', marginLeft: 2 }}>
        <Button
          type="submit"
          form="add-edit-form"
          color="primary"
          variant="contained"
          disabled={loading || formSubmitted}
        >
          Save
        </Button>
        {dataId ? (
          <Button
            color="primary"
            variant="contained"
            onClick={() => showDeletePrompt(dataId)}
            disabled={loading || formSubmitted}
          >
            Delete
          </Button>
        ) : null}
        <Button
          color="secondary"
          variant="contained"
          onClick={onCancel}
          disabled={formSubmitted}
        >
          Cancel
        </Button>
      </DialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

AddEditQuizQuestion.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  quizId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  showDeletePrompt: PropTypes.func,
};
AddEditQuizQuestion.defaultProps = {
  showDeletePrompt: () => {},
};

export default AddEditQuizQuestion;
